import React, { useState, useEffect, useCallback } from "react";
import http from "../Utils/axiosConfig";
import { apiMessage } from "../Utils/apiMessage";
import ToastService from "../Utils/ToastService";
import { onlyNumber } from "../Utils/commonFuntion";
import { downloadCsv } from "../Utils/csv";
// The form / table / pagination shell is the same as डेअरी विक्री, so its stylesheet
// is reused as-is; DairyExpense.css only adds the filter row, the category chips
// and this page's dialog.
import "./DairySales.css";
import "./DairyExpense.css";

// Mirrors CATEGORIES in the server's dairy-expense.service.js, which validates the
// codes. The labels live here because display belongs to the panel — keep the two
// lists in step.
const CATEGORIES = [
  ["electricity", "वीज बिल"],
  ["transport", "वाहतूक"],
  ["salary", "पगार"],
  ["repair", "दुरुस्ती"],
  ["feed", "पशुखाद्य खरेदी"],
  ["construction", "बांधकाम"],
  ["other", "इतर"],
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES);

// Ceiling for the CSV request; the server caps at 1000 regardless.
const CSV_LIMIT = 1000;

const IST = "Asia/Kolkata";

// en-CA gives YYYY-MM-DD. toISOString() is UTC and would return *yesterday*
// between 00:00 and 05:30 IST, quietly defaulting the form to the wrong day.
const toISODate = (d = new Date()) => d.toLocaleDateString("en-CA", { timeZone: IST });

const fmt = (v) => (v != null ? Number(v).toFixed(2) : "0.00");
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: IST,
      })
    : "-";

// An expense register is read a month at a time.
const monthStart = () => {
  const [y, m] = toISODate().split("-");
  return `${y}-${m}-01`;
};

const emptyForm = () => ({ date: toISODate(), category: "electricity", amount: "", note: "" });

const DairyExpense = () => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(toISODate());

  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ grand: 0, byCategory: [] });
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [fetching, setFetching] = useState(false);

  const [editing, setEditing] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  // Owns every page-driven fetch including the first, so paging back to page 1
  // refetches instead of leaving the previous page's rows on screen.
  const fetchRecords = useCallback(async (p, f = from, t = to) => {
    setFetching(true);
    try {
      const res = await http.post("dairy-expense/list", { from_date: f, to_date: t, page: p });
      if (res?.code === "DATA_FOUND") {
        setRows(res.data.records);
        setTotals(res.data.totals);
        setPagination(res.data.pagination);
      } else {
        setRows([]);
        // DATA_NOT_FOUND still carries the range totals, which are zero.
        setTotals(res?.data?.totals || { grand: 0, byCategory: [] });
        setPagination(null);
      }
    } catch (err) {
      console.error("Error fetching expenses:", err);
      ToastService.error("खर्च नोंदी मिळवण्यात अयशस्वी");
    } finally {
      setFetching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => {
    fetchRecords(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = () => {
    // Setting page to 1 only refires the effect when the value changes.
    if (page === 1) fetchRecords(1, from, to);
    else setPage(1);
  };

  const validate = (d) => {
    const e = {};
    if (!d.date) e.date = "तारीख आवश्यक आहे";
    if (!d.category) e.category = "प्रकार निवडा";
    if (!d.amount || Number(d.amount) <= 0) e.amount = "रक्कम शून्यापेक्षा जास्त असावी";
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const found = validate(form);
    if (Object.keys(found).length) {
      setErrors(found);
      return;
    }

    setSaving(true);
    try {
      const res = await http.post("dairy-expense/add", form);
      if (res?.code === "ENTRY_ADDED") {
        ToastService.success("खर्च नोंद जतन झाली ✅");
        setForm(emptyForm());
        setErrors({});
        if (page === 1) fetchRecords(1);
        else setPage(1);
      } else {
        ToastService.error(res?.data?.reason || "नोंद जतन झाली नाही ❌");
      }
    } catch (err) {
      console.error("Error saving expense:", err);
      ToastService.error(apiMessage());
    } finally {
      setSaving(false);
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditing((prev) => ({ ...prev, [name]: value }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const saveEdit = async () => {
    const found = validate(editing);
    if (Object.keys(found).length) {
      setEditErrors(found);
      return;
    }

    setBusy(true);
    try {
      const res = await http.post("dairy-expense/update", editing);
      if (res?.code === "ENTRY_UPDATED") {
        ToastService.success("नोंद अद्ययावत झाली ✅");
        setEditing(null);
        // Refetch rather than patching the row: the category totals above the table
        // are range-wide, so an edited amount has to be re-summed server-side.
        fetchRecords(page);
      } else {
        ToastService.error(res?.data?.reason || "बदल जतन झाला नाही ❌");
      }
    } catch (err) {
      console.error("Error updating expense:", err);
      ToastService.error(apiMessage());
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      const res = await http.post("dairy-expense/delete", { id: deleting.id });
      if (res?.code === "ENTRY_DELETED") {
        ToastService.success("नोंद हटवली ✅");
        setDeleting(null);
        // Deleting the last row of a page would otherwise leave it empty.
        const lastOnPage = rows.length === 1 && page > 1;
        if (lastOnPage) setPage((p) => p - 1);
        else fetchRecords(page);
      } else {
        ToastService.error(res?.data?.reason || "नोंद हटवली गेली नाही ❌");
      }
    } catch (err) {
      console.error("Error deleting expense:", err);
      ToastService.error(apiMessage());
    } finally {
      setBusy(false);
    }
  };

  // Downloads the whole filtered range, not just the visible page — a month's
  // register is the point of the file.
  const handleDownload = async () => {
    try {
      const res = await http.post("dairy-expense/list", {
        from_date: from,
        to_date: to,
        page: 1,
        limit: CSV_LIMIT,
      });
      const all = res?.code === "DATA_FOUND" ? res.data.records : [];
      if (!all.length) {
        ToastService.error("या कालावधीत नोंदी नाहीत");
        return;
      }
      if ((res.data.pagination?.total || 0) > all.length) {
        ToastService.error(`पहिल्या ${all.length} नोंदी डाउनलोड होत आहेत; कालावधी लहान करा.`);
      }
      downloadCsv(
        `itar-kharch-${from}-to-${to}.csv`,
        [
          ["तारीख", (r) => fmtDate(r.date)],
          ["प्रकार", (r) => CATEGORY_LABEL[r.category] || r.category],
          ["रक्कम (₹)", (r) => fmt(r.amount)],
          ["तपशील", (r) => r.note || ""],
        ],
        all,
      );
    } catch (err) {
      console.error("Error downloading expenses:", err);
      ToastService.error("डाउनलोड अयशस्वी");
    }
  };

  return (
    <div className="ds-container">
      {/* ── Entry form ── */}
      <form className="ds-form" onSubmit={handleSubmit}>
        <div className="ds-field">
          <input type="date" name="date" value={form.date} onChange={handleChange} className="ds-date-input" />
          {errors.date && <p className="ds-error">{errors.date}</p>}
        </div>

        <div className="ds-field ds-inline">
          <label className="ds-label">प्रकार:</label>
          <select name="category" value={form.category} onChange={handleChange} className="ds-input de-select">
            {CATEGORIES.map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </div>

        <div className="ds-field ds-inline">
          <label className="ds-label">रक्कम:</label>
          <input type="number" name="amount" value={form.amount} onChange={handleChange}
            onKeyDown={onlyNumber} step="any" placeholder="₹" className="ds-input" />
          {errors.amount && <p className="ds-error">{errors.amount}</p>}
        </div>

        <div className="ds-field ds-inline">
          <label className="ds-label">तपशील:</label>
          <input type="text" name="note" value={form.note} onChange={handleChange}
            maxLength={200} placeholder="(ऐच्छिक)" className="ds-input" />
        </div>

        <div className="ds-submit-row">
          <button type="submit" className="ds-submit-btn" disabled={saving}>
            {saving ? "जतन होत आहे..." : "जतन करा"}
          </button>
        </div>
      </form>

      {/* ── Range filter ── */}
      <div className="de-filter-bar">
        <div className="de-filter-group">
          <label>पासून</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="de-filter-group">
          <label>पर्यंत</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button className="ds-submit-btn de-filter-btn" onClick={handleSearch} disabled={fetching}>
          {fetching ? "..." : "शोधा"}
        </button>
        <button className="ds-submit-btn de-filter-btn de-download-btn" onClick={handleDownload}>
          ⬇ डाउनलोड
        </button>
      </div>

      {/* ── Range totals (all pages, not just this one) ── */}
      <div className="de-summary">
        <div className="de-summary-total">
          <span className="de-summary-label">एकूण खर्च</span>
          <span className="de-summary-value">₹{fmt(totals.grand)}</span>
        </div>
        {totals.byCategory.length > 0 && (
          <div className="de-chips">
            {totals.byCategory.map((c) => (
              <span key={c.category} className="de-chip">
                {CATEGORY_LABEL[c.category] || c.category}
                <b>₹{fmt(c.amount)}</b>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── Table ── */}
      <div className="ds-table-section">
        <div className="ds-table-wrap">
          {fetching && rows.length === 0 ? (
            <div className="ds-no-data"><p>लोड होत आहे...</p></div>
          ) : rows.length === 0 ? (
            <div className="ds-no-data"><p>या कालावधीत खर्चाच्या नोंदी नाहीत.</p></div>
          ) : (
            <table className="ds-table">
              <thead>
                <tr>
                  <th>तारीख</th>
                  <th>प्रकार</th>
                  <th>रक्कम (₹)</th>
                  <th>तपशील</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{fmtDate(r.date)}</td>
                    <td>{CATEGORY_LABEL[r.category] || r.category}</td>
                    <td className="ds-amount">{fmt(r.amount)}</td>
                    <td>{r.note || "-"}</td>
                    <td className="de-row-actions">
                      <button className="de-row-btn" onClick={() => { setEditing({ ...r }); setEditErrors({}); }}>
                        संपादन
                      </button>
                      <button className="de-row-btn de-row-btn--danger" onClick={() => setDeleting(r)}>
                        हटवा
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {pagination && pagination.totalPages > 1 && (
          <div className="ds-pagination">
            <button className="ds-page-btn" onClick={() => setPage((p) => p - 1)}
              disabled={page === 1 || fetching}>
              ‹ मागे
            </button>
            <span className="de-page-info">{page} / {pagination.totalPages}</span>
            <button className="ds-page-btn" onClick={() => setPage((p) => p + 1)}
              disabled={page === pagination.totalPages || fetching}>
              पुढे ›
            </button>
          </div>
        )}
      </div>

      {/* ── Edit dialog ── */}
      {editing && (
        <div className="de-modal-overlay" onClick={() => !busy && setEditing(null)}>
          <div className="de-modal" onClick={(e) => e.stopPropagation()}>
            <h3>खर्च संपादन</h3>

            <div className="de-modal-form">
              <div className="de-modal-field">
                <label className="ds-label">तारीख</label>
                <input type="date" name="date" value={editing.date} onChange={handleEditChange} className="ds-input" />
                {editErrors.date && <p className="ds-error">{editErrors.date}</p>}
              </div>

              <div className="de-modal-field">
                <label className="ds-label">प्रकार</label>
                <select name="category" value={editing.category} onChange={handleEditChange} className="ds-input de-select">
                  {CATEGORIES.map(([code, label]) => (
                    <option key={code} value={code}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="de-modal-field">
                <label className="ds-label">रक्कम</label>
                <input type="number" name="amount" value={editing.amount} onChange={handleEditChange}
                  onKeyDown={onlyNumber} step="any" className="ds-input" />
                {editErrors.amount && <p className="ds-error">{editErrors.amount}</p>}
              </div>

              <div className="de-modal-field">
                <label className="ds-label">तपशील</label>
                <input type="text" name="note" value={editing.note || ""} onChange={handleEditChange}
                  maxLength={200} className="ds-input" />
              </div>
            </div>

            <div className="de-modal-actions">
              <button className="ds-submit-btn" onClick={saveEdit} disabled={busy}>
                {busy ? "जतन होत आहे..." : "जतन करा"}
              </button>
              <button className="ds-submit-btn de-btn-secondary" onClick={() => setEditing(null)} disabled={busy}>
                रद्द करा
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ── */}
      {deleting && (
        <div className="de-modal-overlay" onClick={() => !busy && setDeleting(null)}>
          <div className="de-modal" onClick={(e) => e.stopPropagation()}>
            <h3>नोंद हटवायची?</h3>
            <p className="de-modal-text">
              {fmtDate(deleting.date)} — {CATEGORY_LABEL[deleting.category] || deleting.category} — ₹{fmt(deleting.amount)}
            </p>
            <div className="de-modal-actions">
              <button className="ds-submit-btn de-btn-danger" onClick={confirmDelete} disabled={busy}>
                {busy ? "हटवत आहे..." : "हटवा"}
              </button>
              <button className="ds-submit-btn de-btn-secondary" onClick={() => setDeleting(null)} disabled={busy}>
                रद्द करा
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DairyExpense;
